//@flow
import React, {useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';
import {useHistory, useParams} from 'react-router-dom';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import {ArrowBack} from '@material-ui/icons';
import {
  IconButton,
  Grid,
  Typography,
  FormControlLabel,
  Hidden,
  Divider,
  Radio,
  InputAdornment,
} from '@material-ui/core';

import {usePage} from '../../../../page/utils';
import {colors} from '../../../../../assets/styles/colors';
import IosSwitch from '../../../../../elements/IosSwitch/IosSwitch';
import Collapsible from '../../../../../elements/Collapsible/Collapsible';
import CustomTextField from '../../../../../elements/CustomTextField/CustomTextField';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import SnackBar from '../../../../../elements/SnackBar/SnackBar';

import ModalSelectItems from '../../../../marketing/coupons/components/ModalSelectItems/ModalSelectItems';
import CustomSelect from '../../../../../elements/CustomSelect/CustomSelect';
import LoadingSpinner from '../../../../../elements/LoadingSpinner/LoadingSpinner';
import {commaFormat} from '../../../../../utils/number';

import {scrollTop} from '../../../../../utils/dom';
import {useIsFeatureInTier} from '../../../../../utils/component';

import {TaxRequest, taxesInfoValues} from '../../api';
import type {
  Country,
  Currency,
  Tax as TaxEntity,
  Product,
  Category,
  Pagination,
} from '../../../../../entities';
import RowItem from './RowItem/RowItem';
import {useStoreTaxTypes} from '../../hook';
import {useToastActions} from '../../../../toast-alert/utils';
type Props = {|
  isFetching?: boolean,
  country: Country,
  tax?: TaxEntity,
  saveSuccess?: boolean,
  isSaving?: boolean,
  createTax: ({taxRequest: TaxRequest}) => void,
  fetchTax: ({taxId: string}) => void,
  error?: string,
  currency?: Currency,
  productsFilter?: Product[],
  categoriesFilter?: Category[],
  fetchProducts: ({
    queryFilter?: {[key: string]: string | any},
  }) => void,
  fetchCategoriesSearch: ({
    queryFilter?: {[key: string]: string | any},
  }) => void,
  isFetchingProducts?: boolean,
  isFetchingCategories?: boolean,
  paginationProducts?: Pagination,
  paginationCategories?: Pagination,
  resetProductsFilter: () => void,
  resetCategoriesFilter: () => void,
  resetTax: () => void,
  updateTax: ({taxRequest: TaxRequest, taxId: string}) => void,
|};

const useStyles = makeStyles(theme => ({
  couponWrap: {
    marginTop: theme.spacing(2),
  },
  gridFormulario: {
    padding: theme.spacing(1),
  },
  checkbox: {
    borderRadius: '8px',
  },
  headerWrapper: {
    width: '100%',
    padding: theme.spacing(0, 0, 2, 0),
    background: '#f8f9fb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  headerListItems: {
    background: colors.grey200,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderRadius: '12px 12px 0 0',
    padding: '12px',
    marginTop: '16px',
    flexWrap: 'wrap',
    borderBottom: '1px solid #E8E8ED',
  },
  contentList: {
    borderRadius: '12px',
    background: colors.grey100,
  },
  listItemsWrapper: {
    overflow: 'auto',
    padding: '16px',
    maxHeight: '530px',
  },
  deleteItems: {
    color: '#E13065',
    fontWeight: 600,
    fontSize: '12px',
    lineHeight: '16px',
  },

  backButton: {
    background: 'white',
    padding: '4px',
    margin: '0 16px 0 0',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
  },
  descriptionWrapper: {
    padding: 0,
    paddingLeft: '24px',
  },
  saveButton: {
    marginBottom: theme.spacing(3),
    borderRadius: '32px',
    [theme.breakpoints.down('sm')]: {
      zIndex: 1,
      position: 'fixed',
      left: 0,
      bottom: 64,
      margin: theme.spacing(2),
      height: 48,
      width: `calc(100% - ${theme.spacing(4)})`,
      fontSize: '1.125rem',
    },
  },
  title: {
    fontSize: '18px',
    lineHeight: '24px',
    fontWeight: 600,
    color: 'black',
  },
  cardWrap: {
    margin: '16px 0',
    background: '#FFFFFF',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    borderRadius: '12px',
    padding: '24px',
  },
  grayCardWrap: {
    background: '#F4F4F6',
    border: '1px solid #E8E8ED',
  },
  maxWidth: {
    maxWidth: '600px',
  },
  labelText: {
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: '24px',
    color: colors.grey900,
    paddingBottom: '16px',
  },
  optionalLabelText: {
    color: colors.grey800,
    fontSize: 14,
    fontWeight: 400,
  },
  button: {
    borderRadius: '8px',
    marginTop: '12px',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '20px',
  },
  typesWrap: {
    display: 'flex',
    flexDirection: 'column',
  },
  radioButton: {
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '22px',
    color: colors.grey800,
  },
  radioButtonSelected: {
    fontWeight: 500,
    color: `${colors.grey900} !important`,
  },
  textError: {
    color: '#f44336',
    fontSize: '0.75rem',
    marginTop: '3px',
    textAlign: 'left',
    fontWeight: 400,
    lineHeight: '1.66',
  },
  radioButtonLabel: {
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '20px',
    color: colors.grey800,
  },
  gridContainer: {
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
    },
  },
  stickyCard: {
    width: '100%',
    maxWidth: 'calc(40% - 182px)',
    top: '212px',
    right: '24px',
    position: 'fixed',
    [theme.breakpoints.down('sm')]: {
      position: 'initial',
      maxWidth: '600px',
    },
  },
  divider: {
    border: '1px solid #E8E8ED',
    color: colors.grey300,
    margin: '16px 0',
  },
  floatingBottom: {
    position: 'absolute',
    bottom: 64,
    left: 0,
    right: 0,
  },
  floatingBottomContent: {
    background: '#FFFFFF',
    height: 'fit-content',
    padding: 32,
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
  },
  normalBoldText: {
    color: colors.grey900,
    fontWeight: 500,
    fontSize: 14,
  },
  normalText: {
    color: colors.grey900,
    fontWeight: 400,
    fontSize: 14,
  },
  titleSummary: {
    color: colors.grey800,
    fontWeight: 600,
    fontSize: 14,
  },
  listSummary: {
    marginTop: theme.spacing(1),
    paddingLeft: theme.spacing(2),
  },
  itemListSummary: {
    color: colors.grey800,
    fontWeight: 400,
    fontSize: 14,
    margin: theme.spacing(1),
  },
  switchLabel: {
    color: colors.grey800,
    fontWeight: 700,
    fontSize: 14,
  },
  wrapInputTax: {
    display: 'flex',
    [theme.breakpoints.up('md')]: {
      alignItems: 'center',
      flexDirection: 'row',
    },
  },
  inputTax: {
    width: 115,
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  inputTaxText: {
    fontSize: 10,
    color: colors.grey900,
    marginTop: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      marginTop: 0,
      marginLeft: 20,
      fontSize: 11,
    },
  },
  center: {
    display: 'flex',
    alignItems: 'center',
  },
  endAdornment: {
    marginTop: '0',
    position: 'absolute',
    marginLeft: '90%',
    [theme.breakpoints.up('md')]: {
      marginLeft: '70%',
    },
  },
  wrapperItem: {
    width: '100%',
  },
  rowItem: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  archiveButton: {
    color: '#E13065',
  },
}));

export default usePage<Props>(Tax, 'Tax');

type TaxCriteria = 'PRODUCT' | 'PRODUCT_CATEGORY' | 'ORDER';
type AssignBy = 'PRODUCT' | 'ORDER';

const DEFAULT_EXAMPLE_PRICE = 49;
function Tax(props: Props) {
  const {
    isFetching,
    country,
    tax,
    isSaving,
    saveSuccess,
    error,
    currency,
    productsFilter,
    categoriesFilter,
    fetchProducts,
    fetchCategoriesSearch,
    isFetchingProducts,
    isFetchingCategories,
    paginationProducts,
    paginationCategories,
    resetProductsFilter,
    resetCategoriesFilter,
    createTax,
    fetchTax,
    resetTax,
    updateTax,
  } = props;

  const classes = useStyles();
  const history = useHistory();
  const {taxId} = useParams();

  const {showToast} = useToastActions();

  const [enabled, setEnabled] = useState(true);
  const [taxType, setTaxType] = useState<any>('');
  const [assignBy, setAssignBy] = useState<AssignBy>('PRODUCT');
  const [taxCriteria, setTaxCriteria] = useState<TaxCriteria>('ORDER');
  const [openModal, setOpenModal] = useState(false);

  const [listProductsItems, setListProductsItems] = useState([]);
  const [listCategoryItems, setListCategoryItems] = useState([]);
  const [listItems, setListItems] = useState([]);

  const [openExcludedModal, setOpenExcludedModal] = useState(false);
  const [excludedProducts, setExcludedProducts] = useState([]);
  const [excludedProductsCriteria, setExcludedProductsCriteria] = useState<
    TaxCriteria | ''
  >('');

  const [errorItems, setErrorItems] = useState(false);
  const {handleSubmit, register, errors, watch, setValue} = useForm();
  const watchAllValues = watch();

  const hasTaxesFeature = useIsFeatureInTier('TAXES');

  const {loading, taxTypes} = useStoreTaxTypes();

  const isPeruCountry = country && country.code === 'PE';

  useEffect(() => {
    if (!hasTaxesFeature) history.replace('/configure/taxes');
  }, [hasTaxesFeature, history]);

  useEffect(() => {
    if (taxId && taxId !== 'new') fetchTax({taxId});
  }, [taxId, fetchTax]);

  useEffect(() => {
    if (saveSuccess) {
      history.push(`/configure/taxes`);
    }
  }, [history, saveSuccess]);

  useEffect(() => {
    if (taxCriteria === 'ORDER' || listItems.length || assignBy === 'ORDER')
      setErrorItems(false);
  }, [taxCriteria, listItems, assignBy]);

  useEffect(() => {
    if (tax) {
      const name = tax.name;
      const taxType = taxTypes.find(el => el.name === name);
      if (taxType) setTaxType(taxType.name);
      setEnabled(tax.enabled);
      if (tax.taxCriteria) {
        setTaxCriteria(tax.taxCriteria);
        if (tax.taxCriteria === 'PRODUCT' && tax.taxProducts) {
          const listProducts: Object[] = tax.taxProducts
            .filter(tp => tp.included)
            .map(tp => tp.product);
          setListItems(listProducts);
        } else if (
          tax.taxCriteria === 'PRODUCT_CATEGORY' &&
          tax.taxProductCategories
        ) {
          const listCategories: Object[] = tax.taxProductCategories.map(
            tc => tc.productCategory
          );
          setListItems(listCategories);
        }
        const excludedProducts = tax.taxProducts
          .filter(tp => !tp.included)
          .map(tp => tp.product);
        if (excludedProducts.length) {
          setExcludedProductsCriteria('PRODUCT');
          setExcludedProducts(excludedProducts);
        }
      }
    }
  }, [tax, taxTypes]);

  useEffect(() => {
    return () => resetTax();
  }, [resetTax]);

  const handleDeleteListItem = (id: string) => {
    const newList: Object[] = listItems.filter(el => el.id !== id);
    setListItems(newList);
  };
  const handleDeleteExcludeItem = (id: string) => {
    const newList: Object[] = excludedProducts.filter(el => el.id !== id);
    setExcludedProducts(newList);
  };

  const handleEnableTax = e => {
    setEnabled(e.target.checked);
  };

  const taxSummary = (
    <div>
      <Hidden smDown>
        <Typography className={classes.titleSummary}>
          {!!taxType && isPeruCountry
            ? taxType
            : watchAllValues
            ? watchAllValues.name
            : ''}
        </Typography>
      </Hidden>
      <ul className={classes.listSummary}>
        {watchAllValues.percentage && (
          <li className={classes.itemListSummary}>
            {watchAllValues.percentage}%
          </li>
        )}

        <li className={classes.itemListSummary}>{`Asignar por: ${
          assignBy === 'PRODUCT' ? 'Producto' : 'Monto'
        }`}</li>
        {assignBy === 'PRODUCT' && (
          <li className={classes.itemListSummary}>{`Aplica a: ${
            taxCriteria === 'ORDER'
              ? 'Toda la orden'
              : taxCriteria === 'PRODUCT'
              ? 'Productos específicos'
              : 'Categorias específicas'
          }`}</li>
        )}
        {assignBy === 'ORDER' &&
          watchAllValues &&
          watchAllValues.minOrderAmountValue &&
          currency && (
            <li className={classes.itemListSummary}>{`Mayores a: ${commaFormat(
              watchAllValues.minOrderAmountValue,
              currency.symbol
            )}`}</li>
          )}
      </ul>
      <FormControlLabel
        control={
          <IosSwitch
            className={classes.switchContainer}
            checked={enabled}
            onClick={handleEnableTax}
            disabled={isSaving}
          />
        }
        label={
          <div className={classes.switchLabel} style={{fontWeight: 600}}>
            {enabled ? 'Impuesto activado' : 'Impuesto desactivado'}
          </div>
        }
      />
    </div>
  );

  const onSubmit = values => {
    if (
      !listItems.length &&
      taxCriteria !== 'ORDER' &&
      assignBy === 'PRODUCT'
    ) {
      setErrorItems(true);
      scrollTop('smooth', undefined, 9999);
    } else {
      const selected = taxTypes.find(el => el.name === taxType);
      if (!selected) {
        showToast({
          title: 'Completa los campos',
          type: 'warning',
          message: 'Selecciona un tipo de impuesto',
        });
        return;
      }

      const taxValueObj = taxesInfoValues.find(
        el => el.label === selected.name
      );

      const percentageNumber = isNaN(Number(values.percentage))
        ? Number(taxValueObj?.value || 0)
        : Number(values.percentage);

      const taxRequest: TaxRequest = {
        name: selected?.name,
        label: selected.label,
        taxTypeId: selected.id,
        enabled,
        taxCriteria,
        percentage: (percentageNumber / 100).toString(),
      };

      if (assignBy === 'PRODUCT') {
        if (taxCriteria === 'PRODUCT_CATEGORY') {
          const pCategories = listItems.map((c: Category) => c.id);
          taxRequest.productCategories = pCategories;
        }
        if (taxCriteria === 'PRODUCT') {
          const products = listItems.map((c: Product) => c.id);
          taxRequest.includedProducts = products;
        }
      }
      if (['PRODUCT_CATEGORY', 'ORDER'].includes(taxCriteria)) {
        taxRequest.excludedProducts = excludedProducts.map(p => p.id);
      }
      if (tax) updateTax({taxRequest, taxId: tax.id});
      else createTax({taxRequest});
    }
  };

  const onTaxTypeChange = e => {
    const taxValueObj = taxesInfoValues.find(el => el.label === e.target.value);
    if (taxValueObj?.value) {
      if (tax?.label !== e.target.value)
        setValue('percentage', taxValueObj.value);
    }

    setTaxType(e.target.value);
  };

  return (
    <div className={classes.couponWrap}>
      {(isSaving ||
        loading ||
        isFetching ||
        !currency ||
        (!tax && taxId !== 'new')) && <LoadingSpinner />}
      <SnackBar
        mode="error"
        className={classes.snackBarError}
        message={error}
      />

      <Grid container alignContent="flex-start">
        <Grid item xs={12} className={classes.gridFormulario}>
          <div className={classes.headerWrapper}>
            <Typography className={classes.title}>
              <IconButton
                className={classes.backButton}
                onClick={() =>
                  tax ? history.push(`/configure/taxes`) : history.goBack()
                }
              >
                <ArrowBack />
              </IconButton>
              {!isFetching
                ? tax
                  ? 'Editar configuración de impuesto'
                  : 'Nueva configuración de impuesto'
                : ''}
            </Typography>
            {/* {tax && (
              <CustomButton
                className={classes.archiveButton}
                // onClick={handleModalDelete}
                startIcon={<DeleteOutline />}
              >
                Archivar impuesto
              </CustomButton>
            )} */}
          </div>
          <Grid container spacing={2} className={classes.gridContainer}>
            {!isSaving && !isFetching && (tax || taxId === 'new') && currency && (
              <>
                <Grid item xs={12} md={8}>
                  <form
                    className={classes.maxWidth}
                    noValidate
                    // onSubmit={handleSubmit(onSubmit)}
                  >
                    <div className={classes.cardWrap}>
                      <div style={{marginBottom: '16px'}}>
                        <Typography className={classes.labelText}>
                          Tipo de impuesto
                        </Typography>
                        {isPeruCountry ? (
                          <CustomSelect
                            native
                            fullWidth
                            value={taxType}
                            name="taxType"
                            onChange={onTaxTypeChange}
                            disabled={isSaving}
                          >
                            <option value="" hidden />
                            {taxTypes.map(t => (
                              <option key={t.name} value={t.name}>
                                {t.label}
                              </option>
                            ))}
                          </CustomSelect>
                        ) : (
                          <CustomTextField
                            name="name"
                            label="Nombre del impuesto"
                            inputRef={register({
                              required: 'Campo obligatorio',
                            })}
                            defaultValue={tax ? tax.name : ''}
                            error={!!errors.name}
                            helperText={errors.name ? errors.name.message : ''}
                          />
                        )}
                      </div>
                      <div
                        className={`${classes.typesWrap} ${classes.wrapInputTax}`}
                      >
                        <CustomTextField
                          name="percentage"
                          className={classes.inputTax}
                          defaultValue={
                            tax ? (Number(tax.percentage) * 100).toFixed(0) : ''
                          }
                          inputRef={register({
                            required: 'Incorrecto',
                            pattern: {
                              value: /^[0-9][0-9]?$|^100$/,
                              message: 'Incorrecto',
                            },
                          })}
                          error={!!errors.percentage}
                          helperText={
                            errors.percentage ? errors.percentage.message : ''
                          }
                          disabled
                          InputProps={{
                            endAdornment: (
                              <InputAdornment
                                position="end"
                                className={classes.endAdornment}
                              >
                                %
                              </InputAdornment>
                            ),
                          }}
                        />
                        <Typography className={classes.inputTaxText}>
                          *Los impuestos no están incluidos dentro del precio
                          del producto. <br />
                          {watchAllValues &&
                          !isNaN(watchAllValues.percentage) &&
                          Number(watchAllValues.percentage) > 0 &&
                          currency
                            ? `Ej. Precio de venta: ${commaFormat(
                                DEFAULT_EXAMPLE_PRICE,
                                currency.symbol
                              )} - Valor de venta:
                            ${commaFormat(
                              parseFloat(
                                ((100 + Number(watchAllValues.percentage)) /
                                  100) *
                                  DEFAULT_EXAMPLE_PRICE
                              ),
                              currency.symbol
                            )} - Impuesto: ${commaFormat(
                                (Number(watchAllValues.percentage) *
                                  DEFAULT_EXAMPLE_PRICE) /
                                  100,
                                currency.symbol
                              )}`
                            : null}
                        </Typography>
                      </div>
                      <Divider className={classes.divider} />
                      <div className={classes.typesWrap}>
                        <Typography
                          className={classes.labelText}
                          style={{paddingBottom: 0}}
                        >
                          Asignar por
                        </Typography>
                        <FormControlLabel
                          value={assignBy}
                          control={
                            <Radio
                              checked={assignBy === 'PRODUCT'}
                              size="small"
                              color="primary"
                              onChange={() => setAssignBy('PRODUCT')}
                            />
                          }
                          label={
                            <span
                              className={`${
                                assignBy === 'PRODUCT'
                                  ? classes.radioButtonSelected
                                  : classes.radioButtonLabel
                              }`}
                            >
                              Producto
                            </span>
                          }
                        />
                      </div>
                    </div>
                    {assignBy === 'PRODUCT' && (
                      <div className={classes.cardWrap}>
                        <div className={classes.typesWrap}>
                          <Typography className={classes.labelText}>
                            Aplica a
                          </Typography>
                          <FormControlLabel
                            control={
                              <Radio
                                size="small"
                                color="primary"
                                checked={taxCriteria === 'ORDER'}
                                onChange={() => setTaxCriteria('ORDER')}
                              />
                            }
                            label={
                              <span
                                className={`${
                                  taxCriteria === 'ORDER'
                                    ? classes.radioButtonSelected
                                    : classes.radioButtonLabel
                                }`}
                              >
                                Todos los productos
                              </span>
                            }
                          />
                          <FormControlLabel
                            control={
                              <Radio
                                size="small"
                                color="primary"
                                checked={taxCriteria === 'PRODUCT'}
                                onChange={() => {
                                  setListCategoryItems(listItems);
                                  setListItems(listProductsItems);
                                  setTaxCriteria('PRODUCT');
                                }}
                              />
                            }
                            label={
                              <span
                                className={`${
                                  taxCriteria === 'PRODUCT'
                                    ? classes.radioButtonSelected
                                    : classes.radioButtonLabel
                                }`}
                              >
                                Productos específicos
                              </span>
                            }
                          />
                          <FormControlLabel
                            control={
                              <Radio
                                size="small"
                                color="primary"
                                checked={taxCriteria === 'PRODUCT_CATEGORY'}
                                onChange={() => {
                                  setListProductsItems(listItems);
                                  setListItems(listCategoryItems);
                                  setTaxCriteria('PRODUCT_CATEGORY');
                                }}
                              />
                            }
                            label={
                              <span
                                className={`${
                                  taxCriteria === 'PRODUCT_CATEGORY'
                                    ? classes.radioButtonSelected
                                    : classes.radioButtonLabel
                                }`}
                              >
                                Categorías específicas
                              </span>
                            }
                          />

                          {errorItems && (
                            <span className={classes.textError}>
                              Debe seleccionar al menos 1{' '}
                              {taxCriteria === 'PRODUCT'
                                ? 'producto.'
                                : 'categoría.'}
                            </span>
                          )}
                          {(taxCriteria === 'PRODUCT' ||
                            taxCriteria === 'PRODUCT_CATEGORY') && (
                            <CustomButton
                              fullWidth
                              variant="outlined"
                              color="primary"
                              size="large"
                              className={classes.button}
                              onClick={() => setOpenModal(true)}
                            >
                              {`Seleccionar ${
                                taxCriteria === 'PRODUCT'
                                  ? 'productos'
                                  : 'categorías'
                              }`}
                            </CustomButton>
                          )}
                          {listItems.length > 0 &&
                            (taxCriteria === 'PRODUCT' ||
                              taxCriteria === 'PRODUCT_CATEGORY') && (
                              <div className={classes.contentList}>
                                <Grid
                                  container
                                  spacing={1}
                                  className={classes.listItemsWrapper}
                                >
                                  {listItems.map((p, index: number) => (
                                    <Grid
                                      item
                                      xs={12}
                                      key={index}
                                      className={classes.item}
                                    >
                                      <div className={classes.wrapperItem}>
                                        <RowItem
                                          item={p}
                                          handleDeleteItem={
                                            handleDeleteListItem
                                          }
                                        />
                                        {index < listItems.length - 1 && (
                                          <Divider
                                            className={classes.divider}
                                          />
                                        )}
                                      </div>
                                    </Grid>
                                  ))}
                                </Grid>
                              </div>
                            )}
                        </div>
                      </div>
                    )}

                    {(taxCriteria === 'ORDER' ||
                      taxCriteria === 'PRODUCT_CATEGORY') && (
                      <div
                        className={`${classes.cardWrap} ${classes.grayCardWrap}`}
                      >
                        <div className={classes.typesWrap}>
                          <div
                            className={classes.center}
                            style={{justifyContent: 'space-between'}}
                          >
                            <Typography
                              className={classes.labelText}
                              style={{paddingBottom: 0}}
                            >
                              NO aplica a
                            </Typography>
                            <Typography className={classes.optionalLabelText}>
                              (Opcional)
                            </Typography>
                          </div>
                          <FormControlLabel
                            control={
                              <Radio
                                size="small"
                                color="primary"
                                checked={excludedProductsCriteria === 'PRODUCT'}
                                onChange={() =>
                                  setExcludedProductsCriteria('PRODUCT')
                                }
                              />
                            }
                            label={
                              <span
                                className={`${
                                  excludedProductsCriteria === 'PRODUCT'
                                    ? classes.radioButtonSelected
                                    : classes.radioButtonLabel
                                }`}
                              >
                                Productos específicos
                              </span>
                            }
                          />

                          {excludedProductsCriteria === 'PRODUCT' && (
                            <CustomButton
                              fullWidth
                              variant="outlined"
                              color="primary"
                              size="large"
                              className={classes.button}
                              onClick={() => setOpenExcludedModal(true)}
                            >
                              Seleccionar productos
                            </CustomButton>
                          )}

                          {excludedProducts.length > 0 && (
                            <Grid
                              container
                              spacing={1}
                              className={classes.listItemsWrapper}
                            >
                              {excludedProducts.map((p, index: number) => (
                                <Grid
                                  item
                                  xs={12}
                                  key={index}
                                  className={classes.item}
                                >
                                  <div className={classes.wrapperItem}>
                                    <RowItem
                                      item={p}
                                      handleDeleteItem={handleDeleteExcludeItem}
                                    />
                                    {index < excludedProducts.length - 1 && (
                                      <Divider className={classes.divider} />
                                    )}
                                  </div>
                                </Grid>
                              ))}
                            </Grid>
                          )}
                        </div>
                      </div>
                    )}
                  </form>
                </Grid>
                <Grid item xs={12} md={4} style={{position: 'relative'}}>
                  <Hidden mdUp>
                    <Collapsible
                      startClosed
                      className={classes.maxWidth}
                      title={
                        <div>
                          <div>Resumen de impuesto</div>
                          <Typography className={classes.titleSummary}>
                            {!!taxType && isPeruCountry
                              ? taxType
                              : watchAllValues
                              ? watchAllValues.name
                              : ''}
                          </Typography>
                        </div>
                      }
                    >
                      {taxSummary}
                    </Collapsible>
                  </Hidden>
                  <Hidden smDown>
                    <div
                      className={`${classes.maxWidth} ${classes.stickyCard} `}
                    >
                      <div className={classes.cardWrap}>
                        <div>
                          <Typography
                            className={classes.labelText}
                            style={{padding: 0, margin: 0}}
                          >
                            Resumen del impuesto
                          </Typography>
                          <Divider className={classes.divider} />
                          {taxSummary}
                        </div>
                      </div>
                      <Hidden smDown>
                        <div
                          className={classes.cardWrap}
                          style={{padding: '16px'}}
                        >
                          <CustomButton
                            type="submit"
                            fullWidth
                            variant="contained"
                            color="primary"
                            size="large"
                            className={classes.saveButton}
                            disabled={!hasTaxesFeature || isSaving}
                            style={{margin: 0}}
                            onClick={handleSubmit(onSubmit)}
                          >
                            {tax ? 'Guardar cambios' : 'Crear Impuesto'}
                          </CustomButton>
                        </div>
                      </Hidden>
                    </div>
                  </Hidden>
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
      </Grid>
      <ModalSelectItems
        title="Agregar Productos"
        open={openModal}
        handleClose={() => setOpenModal(false)}
        items={taxCriteria === 'PRODUCT' ? productsFilter : categoriesFilter}
        type={taxCriteria === 'PRODUCT' ? 'PRODUCT' : 'PRODUCT_CATEGORY'}
        pagination={
          taxCriteria === 'PRODUCT' ? paginationProducts : paginationCategories
        }
        isFetchingItems={
          taxCriteria === 'PRODUCT' ? isFetchingProducts : isFetchingCategories
        }
        fetchCategories={fetchCategoriesSearch}
        fetchProducts={fetchProducts}
        setListItems={value => {
          setListItems(value);
          if (taxCriteria === 'PRODUCT') setListProductsItems(value);
          else setListCategoryItems(value);
        }}
        listItems={listItems}
        resetListFilter={
          taxCriteria === 'PRODUCT'
            ? resetProductsFilter
            : resetCategoriesFilter
        }
      />
      <ModalSelectItems
        title="Excluir productos"
        open={openExcludedModal}
        handleClose={() => setOpenExcludedModal(false)}
        items={productsFilter}
        type="PRODUCT"
        pagination={paginationProducts}
        isFetchingItems={isFetchingProducts}
        fetchCategories={fetchCategoriesSearch}
        fetchProducts={fetchProducts}
        setListItems={setExcludedProducts}
        listItems={excludedProducts}
        resetListFilter={resetProductsFilter}
      />

      <Hidden mdUp>
        <div className={classes.floatingBottom}>
          <div className={classes.floatingBottomContent}>
            <CustomButton
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              className={classes.saveButton}
              disabled={!hasTaxesFeature || isSaving}
              style={{margin: '0 16px'}}
              onClick={handleSubmit(onSubmit)}
            >
              {tax ? 'Guardar cambios' : 'Crear Impuesto'}
            </CustomButton>
          </div>
        </div>
      </Hidden>
    </div>
  );
}

Tax.propTypes = {
  isFetching: PropTypes.bool,
  country: PropTypes.object,
  tax: PropTypes.object,
  isSaving: PropTypes.bool,
  error: PropTypes.bool,
  currency: PropTypes.object,
  saveSuccess: PropTypes.bool,
  fetchProducts: PropTypes.func,
  fetchCategoriesSearch: PropTypes.func,
  isFetchingProducts: PropTypes.bool,
  isFetchingCategories: PropTypes.bool,
  paginationProducts: PropTypes.object,
  paginationCategories: PropTypes.object,
  resetProductsFilter: PropTypes.func,
  resetCategoriesFilter: PropTypes.func,
  createTax: PropTypes.func.isRequired,
  fetchTax: PropTypes.func.isRequired,
  resetTax: PropTypes.func.isRequired,
  updateTax: PropTypes.func.isRequired,
};
